import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { shopStore } from "../../store/Shop";
import BaseIcon from "../../components/BaseIcon";
import BaseButton from "../../components/BaseButton";
import CountrySelector from "../../components/CountrySelector";
import BaseInput from "../../components/BaseInput";
import INTOUCH_API from "../../services/Api";
import PersonalAccount from "../../store/PersonalAccount";
import HandImage from "../../assets/images/hand.png";
import {
  getCardRecommendation,
  getShopHomeUrl,
  getShopName,
  COUNTRY_TO_SHOP_LOCALE,
  SHOP_GENERIC_IMAGE,
} from "../../utils/shopProducts";
import openExternalUrl from "../../utils/openExternalUrl";
import trackEvent, { storeNameFromUrl } from "../../utils/analytics";
import "./style.scss";

export default observer(function ShopModal() {
  const { t, i18n } = useTranslation();
  const buttonRef = useRef(null);
  const location = useLocation();
  const [bubblePos, setBubblePos] = useState({ left: 0, top: 0 });
  const [handPos, setHandPos] = useState({ left: 0, top: 0 });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // item 2: форма «уведомить меня» вместо простого текста про регион без магазина.
  // E-mail подставляем из аккаунта; у демо-игрока (без логина) его нет — тогда поле
  // показываем и просим ввести, иначе уведомлять было бы некуда.
  const [notifyCountry, setNotifyCountry] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyState, setNotifyState] = useState("idle"); // idle | sending | done | error

  const isOpen = shopStore.isOpen;
  const country = shopStore.selectedCountry;
  const card = shopStore.card;
  const showCountryTutorial =
    isOpen && !country && !shopStore.isCountryTutorialFinished && !dropdownOpen;

  useEffect(() => {
    if (shopStore.isOpen) shopStore.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!showCountryTutorial) return;
    const reposition = () => {
      const el = buttonRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Position fingertip on the chevron (right side of the dropdown button).
      // Hand body extends down-right into open space below the button, leaving
      // the "Выбери страну" text uncovered.
      setHandPos({
        left: rect.right - 30,
        top: rect.top + rect.height / 2 - 4,
      });
      // Bubble sits BELOW the button so that the upward arrow on the bubble's
      // top edge visually points at the button. Clamped to viewport.
      const bubbleWidth = 260;
      setBubblePos({
        left: Math.max(
          12,
          Math.min(rect.left, window.innerWidth - bubbleWidth - 12)
        ),
        top: rect.bottom + 16,
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [showCountryTutorial]);

  if (!isOpen) return null;

  const handleClose = () => {
    shopStore.close();
    setNotifyState("idle");
    setNotifyCountry("");
  };

  const accountEmail = PersonalAccount.user?.email || "";
  const emailToNotify = (accountEmail || notifyEmail).trim();
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailToNotify);
  const canNotify =
    notifyCountry.trim().length >= 2 && emailValid && notifyState !== "sending";

  const submitNotify = () => {
    if (!canNotify) return;
    setNotifyState("sending");
    INTOUCH_API.requestManager(
      "sendShopNotify",
      { country: notifyCountry.trim(), email: emailToNotify },
      () => setNotifyState("done"),
      () => setNotifyState("error")
    );
  };

  const handleSelectCountry = (code) => {
    shopStore.setCountry(code);
    if (!shopStore.isCountryTutorialFinished) {
      shopStore.markCountryTutorialFinished();
    }
  };

  // Текст тизера — по языку интерфейса (RU→по-русски), ссылка — по стране магазина.
  const recommendation = getCardRecommendation(country, card, i18n.language);
  const isCardMode = Boolean(recommendation);
  // Страна выбрана, но у неё нет локали магазина (в т.ч. устаревший код
  // из localStorage, которого больше нет в COUNTRIES) -> unsupported.
  const isUnsupportedRegion =
    Boolean(country) && !COUNTRY_TO_SHOP_LOCALE[country];

  const handleWatch = () => {
    const url = recommendation
      ? recommendation.url
      : getShopHomeUrl(country) || getShopHomeUrl("RU");
    // GA4 «Stats v2»: финальная кнопка перехода в магазин — карточка, страна, магазин.
    // store_name — по локали страны (URL ведёт через партнёрский редиректор,
    // его хост магазином не является)
    trackEvent("store_click", {
      event_category: "Outbound Link",
      card_id: card ? card.questionId ?? card.id ?? null : null,
      chosen_country: country || null,
      store_name: getShopName(country) || storeNameFromUrl(url),
    });
    openExternalUrl(url);
    handleClose();
  };

  return (
    <div className="shop-modal__overlay" onClick={handleClose}>
      <div
        className="shop-modal__container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shop-modal__header">
          <CountrySelector
            value={country}
            onChange={handleSelectCountry}
            onOpenChange={setDropdownOpen}
            buttonRef={buttonRef}
          />
          <button
            type="button"
            className="shop-modal__close"
            onClick={handleClose}
            aria-label="close"
          >
            <BaseIcon name="close_alert" />
          </button>
        </div>

        {!country && (
          <div className="shop-modal__body shop-modal__body--placeholders">
            <div className="shop-modal__image-wrap shop-modal__image-wrap--empty" />
            <div className="shop-modal__dots">…</div>
            <BaseButton onClick={handleWatch} disabled>
              {t("shop_modal.watch")}
            </BaseButton>
          </div>
        )}

        {country && isUnsupportedRegion && (
          <div className="shop-modal__body shop-modal__body--empty">
            {notifyState === "done" ? (
              <div className="shop-modal__empty-text">
                {t("shop_modal.notify_done")}
              </div>
            ) : (
              <>
                <div className="shop-modal__empty-text">
                  {t("shop_modal.notify_text")}
                </div>
                <div className="shop-modal__notify">
                  <BaseInput
                    value={notifyCountry}
                    onChange={(e) => setNotifyCountry(e.target.value.slice(0, 60))}
                    placeholder={t("shop_modal.notify_country")}
                    name="shop-notify-country"
                  />
                  {!accountEmail && (
                    <BaseInput
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value.slice(0, 254))}
                      placeholder={t("shop_modal.notify_email")}
                      name="shop-notify-email"
                    />
                  )}
                  {notifyState === "error" && (
                    <div className="shop-modal__notify-error">
                      {t("shop_modal.notify_error")}
                    </div>
                  )}
                  <BaseButton disabled={!canNotify} onClick={submitNotify}>
                    {t("shop_modal.notify_btn")}
                  </BaseButton>
                </div>
              </>
            )}
          </div>
        )}

        {country && !isUnsupportedRegion && isCardMode && (
          <div className="shop-modal__body">
            <div className="shop-modal__image-wrap">
              <img src={recommendation.image} alt="" />
            </div>
            <div className="shop-modal__text">
              {recommendation.text || t("shop_modal.card_text_default")}
            </div>
            <BaseButton onClick={handleWatch}>
              {t("shop_modal.watch")}
            </BaseButton>
          </div>
        )}

        {country && !isUnsupportedRegion && !isCardMode && (
          <div className="shop-modal__body">
            <div className="shop-modal__image-wrap shop-modal__image-wrap--generic">
              <img src={SHOP_GENERIC_IMAGE} alt="" />
            </div>
            <div className="shop-modal__text">
              {t("shop_modal.generic_text")}
            </div>
            <BaseButton onClick={handleWatch}>
              {t("shop_modal.watch")}
            </BaseButton>
          </div>
        )}
      </div>

      {showCountryTutorial && (
        <>
          <div
            className="shop-modal__tutorial-bubble"
            style={{ left: bubblePos.left, top: bubblePos.top }}
          >
            {t("shop_modal.country_tutorial")}
          </div>
          <div
            className="shop-modal__tutorial-hand"
            style={{ left: handPos.left, top: handPos.top }}
          >
            <img src={HandImage} alt="" />
          </div>
        </>
      )}
    </div>
  );
});
